import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import secureLocalStorage from 'react-secure-storage';
import { useAppContext, useHttp, useAuthContext } from '@veda/dashboard-ui';
import LoginPannelloSinistro from '../components/LoginPannelloSinistro';
import { loginEmail as loginEmailOperatore } from '../services/operatoreAuthApi';

/** Home dell'utente in base al ruolo, come dopo un login riuscito. */
function homePerRuolo(ruolo) {
  if (ruolo === 'amministratore_ente') return '/pa/ente';
  if (ruolo === 'operatore_economico') return '/operatore/company';
  return '/ppp';
}

// SPID temporaneamente disabilitato — riabilitare quando il SP sarà accreditato AgID.
// function SpidButton({ onClick, disabled }) {
//   return (
//     <button
//       type="button"
//       onClick={onClick}
//       disabled={disabled}
//       className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg border-2 border-[#0066CC] bg-white hover:bg-blue-50 disabled:opacity-50 transition-colors font-semibold text-[#0066CC] text-sm"
//     >
//       <svg width="28" height="28" viewBox="0 0 64 64" fill="none">
//         <circle cx="32" cy="32" r="32" fill="#0066CC" />
//         <text x="32" y="41" textAnchor="middle" fill="white" fontSize="24" fontWeight="bold" fontFamily="Arial">S</text>
//       </svg>
//       Entra con SPID
//     </button>
//   );
// }

function ErrorBox({ msg }) {
  return (
    <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
      {msg}
    </div>
  );
}

// ─── Form login PA (RUP, Dirigente, Ufficio Tecnico, Amm. Ente) ───────────────

/**
 * Richiesta mail di recupero password.
 *
 * Vale sia per gli utenti PA sia per gli Operatori Economici: entrambi vivono
 * nella tabella `users` e condividono la colonna `password`, quindi il reset di
 * Veda rimette in sesto anche il login su /api/v1/operatore/auth/login.
 */
function RecuperoPassword({ onAnnulla, placeholderEmail = 'Email' }) {
  const http = useHttp();
  const [email, setEmail]     = useState('');
  const [errore, setErrore]   = useState(null);
  const [inviata, setInviata] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setErrore(null);
    await http.request('/sanctum/csrf-cookie', { method: 'get' });
    await http.request('/api/dashboard/forgot-password', {
      method: 'post',
      body: JSON.stringify({ email }),
      onSuccess: () => setInviata(true),
      // Su email sconosciuta l'endpoint risponde 422 "No account found with this
      // email": mostrarlo rivelerebbe quali indirizzi sono registrati. Trattiamo
      // il 422 come esito neutro — il campo è già validato come email dal browser.
      onFormError: () => setInviata(true),
      onError: (err) => setErrore(err instanceof Error ? err.message : 'Errore durante la richiesta.'),
    });
  }

  if (inviata) {
    return (
      <div className="flex flex-col gap-4">
        <div className="px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
          Se l'indirizzo è associato a un account, ti abbiamo inviato una mail con il link per reimpostare la password.
        </div>
        <button type="button" onClick={onAnnulla} className="text-xs text-center text-gray-500 hover:underline">
          ← Torna al login
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Inserisci la tua email: riceverai un link per reimpostare la password.
      </p>
      {errore && <ErrorBox msg={errore} />}
      <input
        type="email" value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder={placeholderEmail} required autoFocus
        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
      />
      <button
        type="submit" disabled={http.loading}
        className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
      >
        {http.loading ? 'Invio in corso…' : 'Invia link di recupero'}
      </button>
      <button type="button" onClick={onAnnulla} className="text-xs text-center text-gray-500 hover:underline">
        ← Torna al login
      </button>
    </form>
  );
}

function LoginPa() {
  const http            = useHttp();
  const { logIn }       = useAuthContext();
  const [form, setForm] = useState({ email: '', password: '' });
  const [errore, setErrore] = useState(null);
  const [recupero, setRecupero] = useState(false);
  // Il server ha rifiutato il login perché sul browser è attiva la sessione di
  // un altro utente (409). Può accadere anche con isLogged falso, ad esempio con
  // un cookie rimasto da un altro ambiente: lì il redirect non scatta.
  const [sessioneAltrui, setSessioneAltrui] = useState(false);

  const aggiorna = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    setErrore(null);
    setSessioneAltrui(false);
    await http.request('/sanctum/csrf-cookie', { method: 'get' });
    await http.request('/api/dashboard/login', {
      method: 'post',
      body: JSON.stringify(form),
      onSuccess: (res) => logIn(res),
      onFormError: (res) => {
        setErrore(res?.message ?? 'Credenziali non valide.');
        if (res?.errore === 'sessione_attiva') setSessioneAltrui(true);
      },
      onError: (err) => setErrore(err instanceof Error ? err.message : 'Credenziali non valide.'),
    });
  }

  // Chiude la sessione altrui rimasta sul browser, così il login può procedere.
  async function esciDallAltroAccount() {
    await http.request('/sanctum/csrf-cookie', { method: 'get' });
    await http.request('/api/dashboard/logout', {
      method: 'post',
      onSuccess: () => { setErrore(null); setSessioneAltrui(false); },
      onFormError: () => setErrore('Impossibile chiudere la sessione precedente.'),
      onError: () => setErrore('Impossibile chiudere la sessione precedente.'),
    });
  }

  if (recupero) {
    return <RecuperoPassword onAnnulla={() => setRecupero(false)} placeholderEmail="Email istituzionale" />;
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      {errore && <ErrorBox msg={errore} />}
      {sessioneAltrui && (
        <button
          type="button" onClick={esciDallAltroAccount} disabled={http.loading}
          className="w-full py-2.5 border border-blue-600 text-blue-600 rounded-lg font-semibold hover:bg-blue-50 disabled:opacity-50 transition-colors text-sm"
        >
          {http.loading ? 'Uscita in corso…' : 'Esci dall\'altro account e riprova'}
        </button>
      )}
      <input
        type="email" name="email" value={form.email} onChange={aggiorna}
        placeholder="Email istituzionale" required autoFocus
        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
      />
      <input
        type="password" name="password" value={form.password} onChange={aggiorna}
        placeholder="Password" required
        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
      />
      <button
        type="submit" disabled={http.loading}
        className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
      >
        {http.loading ? 'Accesso in corso…' : 'Accedi'}
      </button>
      <button
        type="button" onClick={() => setRecupero(true)}
        className="text-xs text-center text-gray-500 hover:underline mt-1"
      >
        Password dimenticata?
      </button>
    </form>
  );
}

// ─── Form login Operatore Economico ───────────────────────────────────────────

function LoginOperatore() {
  const [form, setForm]     = useState({ email: '', password: '' });
  const [errore, setErrore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recupero, setRecupero] = useState(false);

  const aggiorna = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  async function handleSubmit(e) {
    e.preventDefault();
    setErrore(null);
    setLoading(true);
    try {
      const result = await loginEmailOperatore(form.email, form.password);

      if (result.errore) {
        setErrore(result.errore);
        return;
      }

      const { token, user: shape } = result;

      const backend = window.VEDA_API_URL || '';
      await fetch(`${backend}/sanctum/csrf-cookie`, { credentials: 'include' });
      const csrf = decodeURIComponent(
        document.cookie.split('; ').find((c) => c.startsWith('XSRF-TOKEN='))?.split('=')[1] ?? ''
      );
      await fetch(`${backend}/api/v1/operatore/sessione`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-XSRF-TOKEN': csrf,
        },
      });

      secureLocalStorage.setItem('veda_user', JSON.stringify({ ...shape, _sanctum_token: token }));
      window.location.replace('/operatore/company');
    } catch {
      setErrore('Errore durante l\'accesso. Riprova.');
    } finally {
      setLoading(false);
    }
  }

  if (recupero) return <RecuperoPassword onAnnulla={() => setRecupero(false)} />;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      {errore && <ErrorBox msg={errore} />}
      <input
        type="email" name="email" value={form.email} onChange={aggiorna}
        placeholder="Email" required
        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
      />
      <input
        type="password" name="password" value={form.password} onChange={aggiorna}
        placeholder="Password" required
        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm"
      />
      <button
        type="submit" disabled={loading}
        className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
      >
        {loading ? 'Accesso in corso…' : 'Accedi'}
      </button>
      <button
        type="button" onClick={() => setRecupero(true)}
        className="text-xs text-center text-gray-500 hover:underline mt-1"
      >
        Password dimenticata?
      </button>
    </form>
  );
}

// ─── Pagina principale ────────────────────────────────────────────────────────

export default function LoginPage() {
  const { loginImage, currentMode } = useAppContext();
  const { isLogged }                = useAuthContext();
  const [errore, setErrore]         = useState(null);
  const [sezione, setSezione]       = useState('pa'); // 'pa' | 'operatore'

  // Il callback SPID atterra qui con ?token= e deve poter completare lo scambio
  // token→sessione prima di qualunque redirect.
  const callbackInCorso = new URLSearchParams(window.location.search).has('token');

  // Gestisce il callback SPID (token nei query params) — mantenuto per quando
  // SPID verrà riabilitato dopo l'accreditamento AgID.
  useEffect(() => {
    const params  = new URLSearchParams(window.location.search);
    const token   = params.get('token');
    const userRaw = params.get('user');
    const errMsg  = params.get('errore');

    if (errMsg) {
      setErrore(decodeURIComponent(errMsg));
      return;
    }

    if (!token || !userRaw) return;

    let user;
    try {
      const padded = userRaw.padEnd(userRaw.length + (4 - userRaw.length % 4) % 4, '=');
      user = JSON.parse(atob(padded));
    } catch {
      setErrore('Risposta non valida dal provider di identità.');
      return;
    }

    const backend = window.VEDA_API_URL || '';

    fetch(`${backend}/sanctum/csrf-cookie`, { credentials: 'include' })
      .then(() => {
        const csrf = decodeURIComponent(
          document.cookie.split('; ').find((c) => c.startsWith('XSRF-TOKEN='))?.split('=')[1] ?? ''
        );
        const sessionUrl = user.role === 'operatore_economico'
          ? `${backend}/api/v1/operatore/sessione`
          : `${backend}/api/v1/pa/sessione`;

        return fetch(sessionUrl, {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            'X-XSRF-TOKEN': csrf,
          },
        });
      })
      .then((res) => res.ok ? res.json() : user)
      .then((shape) => {
        secureLocalStorage.setItem('veda_user', JSON.stringify({ ...shape, _sanctum_token: token }));

        if (shape.role === 'amministratore_ente') {
          window.location.replace('/pa/ente');
        } else if (shape.role === 'operatore_economico') {
          window.location.replace('/operatore/company');
        } else {
          window.location.replace('/ppp');
        }
      })
      .catch(() => {
        setErrore('Errore durante l\'accesso. Riprova.');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Chi è già autenticato non deve vedere il form di login: lo rimandiamo alla
  // sua home. Senza questo, un utente loggato che torna su /login e prova ad
  // accedere con un altro account riceve un 409 dal server.
  if (isLogged && !callbackInCorso) {
    return <Navigate to={homePerRuolo(isLogged.role)} replace />;
  }

  return (
    <div className={currentMode}>
      <div className="min-h-screen flex dark:bg-main-dark-bg bg-main-bg">

        <LoginPannelloSinistro loginImage={loginImage} />

        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-sm">

            <div className="mb-6">
              <h1 className="text-2xl font-extrabold text-gray-800 dark:text-white mb-1">Accedi</h1>
            </div>

            {errore && (
              <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                {errore}
              </div>
            )}

            {/* Selettore sezione */}
            <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
              <button
                type="button"
                onClick={() => setSezione('pa')}
                className={`flex-1 py-2 text-sm font-semibold transition-colors ${
                  sezione === 'pa'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50'
                }`}
              >
                Ente / RUP
              </button>
              <button
                type="button"
                onClick={() => setSezione('operatore')}
                className={`flex-1 py-2 text-sm font-semibold transition-colors ${
                  sezione === 'operatore'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50'
                }`}
              >
                Operatore Economico
              </button>
            </div>

            {sezione === 'pa' ? <LoginPa /> : <LoginOperatore />}

            <div className="mt-6 pt-6 border-t border-gray-100 dark:border-gray-700 text-center space-y-3">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Vuoi consultare gli avvisi pubblici PPP?</p>
                <a
                  href="/avvisi"
                  className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold text-[#0066CC] hover:underline"
                >
                  Visualizza avvisi pubblici →
                </a>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Ente pubblico o operatore economico senza account:</p>
                <a
                  href="/pa/richiesta-rup"
                  className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-[#0066CC] hover:underline"
                >
                  Richiedi l'accesso
                </a>
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
}
